import type * as React from "react";
import { useState } from "react";

// Third Party
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useQueryClient, useMutation } from "@tanstack/react-query";

// UI
import { Button } from "@/components/ui/button";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import { useToast } from "@/components/ui/use-toast";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { FileUpload } from "@/components/FileUpload";
import { FilePreviewModal } from "@/components/FilePreviewModal";
// Icons
import { ArrowLeft, Save } from "lucide-react";

// Services
import { postDivPlans } from "@/services/tax-obligations";
import { Spinner } from "@/components/ui/spinner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// helpers
import { useDocumentByType, DOC_TYPES } from "@/helpers/documentTypePreview";
import { S3_BUCKET_LINK } from "@/components/constants";

export const Route = createFileRoute(
  "/_layout-private/report/tax-obligations/dividend-plan/add-new"
)({
  component: RouteComponent,
});

function RouteComponent() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const { getDocument } = useDocumentByType();
  const doc = getDocument(DOC_TYPES.DIVIDEND_DISTRIBUTION_PLAN);

  const [formData, setFormData] = useState({
    companyId: "",
    yearly: 0,
    plannedDividend: "",
    totalPhase: 0,
    docFile: "",
  });

  const mutation = useMutation({
    mutationFn: async () => {
      const companyId = JSON.parse(localStorage.getItem("companyId") || "");
      const data = {
        companyId: companyId || "",
        yearly: Number(formData.yearly),
        // Handle decimal numbers properly by removing commas before converting
        plannedDividend: Number(formData.plannedDividend.replace(/,/g, "")),
        docFile: formData.docFile,
        totalPhase: Number(formData.totalPhase),
      };

      await postDivPlans(data);
    },

    onSuccess: () => {
      toast({
        title: "ສຳເລັດ",
        description: "ເພີ່ມແຜນເງິນປັນຜົນ",
        variant: "success",
      });
      queryClient.invalidateQueries({ queryKey: ["dividendPlan"] });
      navigate({
        to: "/report/tax-obligations",
        search: { active: "dividendPlan" as const, startYear: 0, endYear: 0 },
      });
    },
    onError: (error) => {
      console.error("Error adding action plan:", error);
      toast({
        title: "ຫຼົ້ມເຫຼວ",
        description: "ມີບາງຢ່າງຜິດພາດ, ກະລຸນາລອງໃໝ່",
        variant: "destructive",
      });
    },
  });

  const formatNumber = (value: string) => {
    if (!value) return "";
    const parts = value.split(".");
    const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    return parts.length > 1 ? `${integerPart}.${parts[1]}` : integerPart;
  };

  const handleInputChange = (name: string, value: string) => {
    if (name === "plannedDividend") {
      // Remove any non-digit characters except decimal point
      const cleanValue = value.replace(/[^\d.]/g, "");
      const parts = cleanValue.split(".");

      // Ensure only one decimal point and limit to 2 decimal places
      let formattedValue = parts[0];
      if (parts.length > 1) {
        formattedValue += "." + parts[1].slice(0, 2);
      }

      setFormData((prev) => ({
        ...prev,
        [name]: formattedValue,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.docFile) {
      toast({
        title: "ຫຼົ້ມເຫຼວ",
        description: "ກະລຸນາອັບໂຫຼດເອກະສານກ່ອນບັນທຶກ",
        variant: "destructive",
      });

      return;
    }

    mutation.mutate();
  };

  return (
    <>
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink>
              <Link
                replace
                to="/report/tax-obligations"
                search={{
                  active: "dividendPlan" as const,
                  startYear: 0,
                  endYear: 0,
                }}
              >
                ລາຍງານແຜນດຳເນີນງານ
              </Link>
            </BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage>ສ້າງລາຍງານຖານະການເງິນ - ໜີ້ສິນ</BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>
      <Card className="mt-8">
        <CardHeader>
          <button
            type="button"
            onClick={() =>
              navigate({
                to: "/report/tax-obligations",
                search: {
                  active: "dividendPlan" as const,
                  startYear: 0,
                  endYear: 0,
                },
              })
            }
            className="flex gap-1 items-center mb-4"
            aria-label="Go back to tax obligations report"
          >
            <ArrowLeft className="w-5 h-5" />
            ກັບຄືນ
          </button>
          <div className="flex justify-between">
            <div>
              <CardTitle>ຟອມການສ້າງແຜນເງິນປັນຜົນ</CardTitle>
              <CardDescription>ໃບລາຍງານແຜນດຳເນີນງານ</CardDescription>
            </div>
            <FilePreviewModal
              fileUrl={`${S3_BUCKET_LINK}${doc?.documentFile}`}
              fileName={doc?.documentName ?? ""}
              text={doc?.documentName ?? ""}
              description={doc?.description}
              showDownloadButton={true}
              useGoogleViewer={true}
            />
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 items-center w-full max-w-sm">
              <div className="grid gap-1.5">
                <Label htmlFor="yearly" className="flex items-center">
                  ປະຈຳປີ
                  <span className="text-red-500 ml-0.5">*</span>
                </Label>
                {/* <Input
									type="number"
									id="yearly"
									placeholder="ປ້ອນ..."
									className="rounded-md"
									onChange={(e) => handleInputChange("yearly", e.target.value)}
									value={formData.yearly || ""}
								/> */}
                <Select
                  value={
                    formData.yearly ? formData.yearly.toString() : undefined
                  }
                  onValueChange={(value) => {
                    handleInputChange("yearly", Number(value).toString());
                  }}
                  required={true}
                >
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="ກະລຸນາເລືອກປີ" />
                  </SelectTrigger>
                  <SelectContent>
                    {Array.from(
                      { length: new Date().getFullYear() - 1974 },
                      (_, i) => (
                        <SelectItem
                          key={i}
                          value={(new Date().getFullYear() - i).toString()}
                        >
                          {new Date().getFullYear() - i}
                        </SelectItem>
                      )
                    )}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid gap-1.5">
                <Label htmlFor="plannedDividend" className="flex items-center">
                  ແຜນມອບເງິນ
                  <span className="text-red-500 ml-0.5">*</span>
                </Label>

                <Input
                  type="text"
                  id="plannedDividend"
                  placeholder="1,000,000"
                  className="text-right rounded-md"
                  onChange={(e) =>
                    handleInputChange("plannedDividend", e.target.value)
                  }
                  value={formatNumber(formData.plannedDividend)}
                />
              </div>

              <div className="grid gap-1.5">
                <Label htmlFor="totalPhase" className="flex items-center">
                  ຈຳນວນງວດ
                  <span className="text-red-500 ml-0.5">*</span>
                </Label>
                <Input
                  type="number"
                  id="totalPhase"
                  placeholder="ປ້ອນ..."
                  className="rounded-md"
                  onChange={(e) =>
                    handleInputChange("totalPhase", e.target.value)
                  }
                  value={formData.totalPhase || ""}
                />
              </div>
              <div className="my-4">
                <FileUpload
                  currentFile={formData.docFile}
                  onFileUpload={(value) =>
                    setFormData({ ...formData, docFile: value })
                  }
                />
              </div>
            </div>
            <div className="flex justify-start [&>*]w-24 mt-4 gap-x-4">
              <Button
                type="reset"
                className="bg-red-500"
                onClick={() =>
                  navigate({
                    to: "/report/tax-obligations",
                    search: {
                      active: "dividendPlan" as const,
                      startYear: 0,
                      endYear: 0,
                    },
                  })
                }
              >
                ຍົກເລີກ
              </Button>
              <Button disabled={mutation.isPending} type="submit">
                {mutation.isPending ? (
                  <Spinner />
                ) : (
                  <>
                    <Save className="mr-2" /> ບັນທຶກ
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </>
  );
}
